import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";

import { initAnalytics } from "./analytics";
import { App } from "./App";
import { AuthCallback } from "./auth/AuthCallback";
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth } from "./components/RequireAuth";
import { RequirePaid } from "./components/RequirePaid";
import { AccountPage } from "./pages/AccountPage";
import { AlertsPage } from "./pages/AlertsPage";
import { ComparePage } from "./pages/ComparePage";
import { SavedPage } from "./pages/SavedPage";
import { ScorePage } from "./pages/ScorePage";
import { UpgradePage } from "./pages/UpgradePage";
import "./styles.css";

// Feature facebook-login-branded-auth (Req 8): every route is gated by
// RequireAuth (auto-redirect to Managed Login for unauthenticated visitors) —
// the old in-app splash page is gone. TWO routes stay unwrapped:
//   - `/auth/callback` — the OAuth return path; wrapping it would redirect back
//     to Managed Login before the token exchange completes (infinite loop).
//   - `/score/:scoreId` — the PUBLIC shared Tilt Report (#37/#38). It is a
//     lead-gen surface: logged-out visitors must see the teaser (address, map,
//     score + offer range) with the deeper analysis gated behind a "Create a
//     free account" overlay. ScorePage renders auth-aware; the server returns a
//     teaser payload for anon requests. Auto-redirecting here would kill the
//     signup funnel — so it must NOT be wrapped.
// Paid pages nest RequirePaid inside RequireAuth (auth first, then free→/upgrade).
const router = createBrowserRouter([
  { path: "/", element: <RequireAuth><App /></RequireAuth> },
  // NOT wrapped — public shared report with its own teaser + signup gate.
  { path: "/score/:scoreId", element: <ScorePage /> },
  // NOT wrapped — the OAuth callback must never auto-redirect (loop guard).
  { path: "/auth/callback", element: <AuthCallback /> },
  { path: "/upgrade", element: <RequireAuth><UpgradePage /></RequireAuth> },
  { path: "/account", element: <RequireAuth><AccountPage /></RequireAuth> },
  // Paid-only pages: RequireAuth (redirect anon → Managed Login) wraps
  // RequirePaid (redirect free → /upgrade).
  {
    path: "/saved",
    element: (
      <RequireAuth>
        <RequirePaid>
          <SavedPage />
        </RequirePaid>
      </RequireAuth>
    ),
  },
  {
    path: "/compare",
    element: (
      <RequireAuth>
        <RequirePaid>
          <ComparePage />
        </RequirePaid>
      </RequireAuth>
    ),
  },
  {
    path: "/alerts",
    element: (
      <RequireAuth>
        <RequirePaid>
          <AlertsPage />
        </RequirePaid>
      </RequireAuth>
    ),
  },
  // 404 → home.
  { path: "*", element: <RequireAuth><App /></RequireAuth> },
]);

// Before render so the initial pageview and autocapture see the first paint.
// No-op when VITE_POSTHOG_KEY is unset (local dev).
initAnalytics();

const root = document.getElementById("root");
if (!root) throw new Error("#root element not found");

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  </StrictMode>,
);
